import {
    Component,
    computed,
    DestroyRef,
    effect,
    EventEmitter,
    inject,
    input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    signal,
    SimpleChanges,
    untracked
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PureOriginalService} from "../../../service/pure-original.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Subscription} from "rxjs";
import {PureOriginalsMatchingDetailsModel} from "../../../models/PureOriginalsMatchingDetails-model";
import {MostRequestedMissingTuning} from "../../../models/Most-Requested-Tunings";
import {AttachmentService} from "../../../service/attachment.service";
import {AiFinisherService} from "../../../service/ai-finisher.service";
import {NotificationService} from "../../../service/Notification.service";

@Component({
    selector: 'app-pure-original-matching-details',
    templateUrl: './pure-original-matching-details.component.html',
    styleUrls: ['./pure-original-matching-details.component.css']
})
export class PureOriginalMatchingDetailsComponent implements OnInit, OnChanges, OnDestroy {


    subscription: Subscription;
    pureOriginalId = input<number>();
    pureOriginalMatchingIds = input<number[]>([]);
    isClaimedFile = input<boolean>();
    isImpossibleTuningUploaded = input<boolean>();
    error: string;

    private disabledVersionsCheckBox = signal<string[]>([]);
    public versionNameParam = signal<string | null>(null);
    @Output() public versionName: EventEmitter<string> = new EventEmitter<string>();
    @Output() public isFileModalOpened: EventEmitter<boolean> = new EventEmitter<boolean>();

    pureOriginalsService = inject(PureOriginalService);
    route = inject(ActivatedRoute);
    router = inject(Router);
    private attachmentService = inject(AttachmentService);
    private aiFinisherService = inject(AiFinisherService);
    private notificationService = inject(NotificationService);
    private destroyRef = inject(DestroyRef);

    /** Tunings whose "send to AI Finisher" call is in flight — keeps the button disabled per card. */
    aiSeedInFlight = signal<Set<string>>(new Set<string>());

    expandedAttachmentIndex = signal<string | null>(null);
    attachmentCounts = signal<Map<number, number>>(new Map());

    public selectedTab = this.pureOriginalsService.selectedTab;
    public pageSize = 6;
    public currentPage = signal<number>(0);
    public focusedMatchingId = computed(() => this.pureOriginalMatchingIds()
        .find(id => Number.isSafeInteger(id) && id > 0) ?? null);
    private appliedMatchingTarget = '';

    private versionsEffect = effect(() => {
        const completed = this.completedList();
        const partial = this.partialList();
        const allWithVersions = [
            ...completed.filter(v => v.versionId),
            ...partial.filter(v => v.versionId)
        ];
        if (allWithVersions.length > 0) {
            this.loadAttachmentCounts(allWithVersions);
        }
    });

    public completedList = computed<PureOriginalsMatchingDetailsModel[]>(
        () => this.pureOriginalsService.pureOriginalMatchingDetails()
            ? this.pureOriginalsService.sortedListByTuningNames(true) ?? [] : []
    );
    public partialList = computed<PureOriginalsMatchingDetailsModel[]>(
        () => this.pureOriginalsService.pureOriginalMatchingDetails()
            ? this.pureOriginalsService.sortedListByTuningNames(false) ?? [] : []
    );
    public missingList = computed<MostRequestedMissingTuning[]>(
        () => this.pureOriginalsService.pureOriginalMatchingDetails()?.pureOriginalMissingVersions ?? []
    );
    public impossibleList = computed<MostRequestedMissingTuning[]>(
        () => this.pureOriginalsService.pureOriginalMatchingDetails()?.pureOriginalImpossibleTunings ?? []
    );

    private matchingTargetEffect = effect(() => {
        const id = this.focusedMatchingId();
        const key = `${this.pureOriginalId()}:${id}`;
        if (!id) { this.appliedMatchingTarget = ''; return; }
        if (key === this.appliedMatchingTarget) return;
        const lists = [
            {tab: 'completed' as const, ids: this.completedList().map(item => +item.pureOriginalMatchingDetailsId)},
            {tab: 'partial' as const, ids: this.partialList().map(item => +item.pureOriginalMatchingDetailsId)},
            {tab: 'impossible' as const, ids: this.impossibleList().map(item => +item.id)}
        ];
        const target = lists.find(list => list.ids.includes(id));
        if (!target) return; // Wait for the asynchronous matching response.
        this.appliedMatchingTarget = key;
        untracked(() => {
            this.selectedTab.set(target.tab);
            this.currentPage.set(Math.floor(target.ids.indexOf(id) / this.pageSize));
        });
    }, {allowSignalWrites: true});

    public activeLength = computed(() => {
        const tab = this.selectedTab();
        if (tab === 'completed') return this.completedList().length;
        if (tab === 'partial') return this.partialList().length;
        if (tab === 'missing') return this.missingList().length;
        return this.impossibleList().length;
    });

    public totalPages = computed(() => Math.max(1, Math.ceil(this.activeLength() / this.pageSize)));

    private clampPage(page: number) {
        return Math.min(Math.max(0, page), this.totalPages() - 1);
    }

    public pagedCompletedList = computed<PureOriginalsMatchingDetailsModel[]>(() => {
        const page = this.clampPage(this.currentPage());
        const start = page * this.pageSize;
        return this.completedList().slice(start, start + this.pageSize);
    });

    public pagedPartialList = computed<PureOriginalsMatchingDetailsModel[]>(() => {
        const page = this.clampPage(this.currentPage());
        const start = page * this.pageSize;
        return this.partialList().slice(start, start + this.pageSize);
    });

    public pagedMissingList = computed<MostRequestedMissingTuning[]>(() => {
        const page = this.clampPage(this.currentPage());
        const start = page * this.pageSize;
        return this.missingList().slice(start, start + this.pageSize);
    });

    public pagedImpossibleList = computed<MostRequestedMissingTuning[]>(() => {
        const page = this.clampPage(this.currentPage());
        const start = page * this.pageSize;
        return this.impossibleList().slice(start, start + this.pageSize);
    });

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pureOriginalId'] && !changes['pureOriginalId'].firstChange) {
            this.subscription?.unsubscribe();
            this.pureOriginalsService.clearInterval();
            this.pureOriginalsService.pureOriginalMatchingDetails.set(null);
            this.getPureOriginalDetails();
            this.startIntervalOnEvery5Seconds();
        }
        if (changes["isImpossibleTuningUploaded"] && !changes["isImpossibleTuningUploaded"].firstChange) {
            if (this.isImpossibleTuningUploaded()) {
                this.getPureOriginalDetails();
            }
        }
    }

    ngOnInit() {
        this.pureOriginalsService.clearInterval();
        this.pureOriginalsService.pureOriginalMatchingDetails.set(null);
        this.getPureOriginalDetails();
        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
            this.versionNameParam.set(params['versionName']);
        });
        this.startIntervalOnEvery5Seconds();
    }

    getPureOriginalDetails() {
       this.subscription = this.pureOriginalsService.getPureOriginalMatchingDetails(this.pureOriginalId());
    }

    selectTab(tab: 'completed' | 'partial' | 'missing' | 'impossible') {
        if (this.selectedTab() !== tab) {
            this.selectedTab.set(tab);
            this.currentPage.set(0);
        }
    }

    prevPage() {
        this.currentPage.update(p => Math.max(0, p - 1));
    }

    nextPage() {
        this.currentPage.update(p => Math.min(this.totalPages() - 1, p + 1));
    }

    isDownloadInProgress(id: number) {
        return this.pureOriginalsService.isDownloadInProgress(id);
    }

    downloadVersionFile(id: number, winOls: string, tableName: string) {
        this.pureOriginalsService.downloadFileFromDatabase(id, winOls, tableName);
    }

    downloadMissingTuning(id: number, versionName: string, tableName: string) {
        this.pureOriginalsService.downloadFileFromDatabase(id, versionName, tableName);
    }

    openFileUploadModal(versionName?: string) {
        this.isFileModalOpened.emit(true);
        if (versionName) {
            this.versionName.emit(versionName);
        }
    }

    /**
     * Hand this partially tuned tuning to the AI finisher: dcf-api proxies dcf-main-app's seed, which
     * creates an OPEN tuning_handoff (with the input zip + gap data) that dcf-automation claims. The
     * seed is idempotent — re-clicking while a handoff is un-finished returns the existing one.
     */
    sendToAiFinisher(tuningName: string): void {
        const id = this.pureOriginalId();
        if (!id || this.aiSeedInFlight().has(tuningName)) {
            return;
        }
        this.aiSeedInFlight.update(s => {
            const next = new Set(s);
            next.add(tuningName);
            return next;
        });
        this.aiFinisherService.seedFromPureOriginal(id, tuningName).subscribe({
            next: (handoff: any) => {
                this.clearAiSeedFlag(tuningName);
                const already = handoff?.workStatus === 'IN_PROGRESS';
                this.notificationService.showSuccessMessage(
                    `Handoff #${handoff?.id ?? '?'} (${tuningName}) ${already
                        ? 'is already being processed by the AI'
                        : 'queued — the AI will finish it and send a suggestion for review'}`,
                    'AI Finisher');
            },
            error: (err) => {
                this.clearAiSeedFlag(tuningName);
                const msg = typeof err?.error === 'string' && err.error
                    ? err.error
                    : 'Could not send to the AI finisher';
                this.notificationService.showErrorMessage(msg, 'AI Finisher');
            }
        });
    }

    private clearAiSeedFlag(tuningName: string): void {
        this.aiSeedInFlight.update(s => {
            const next = new Set(s);
            next.delete(tuningName);
            return next;
        });
    }

    checkIfVersionIsInQueue(versionName: string) {
        return this.pureOriginalsService.pureOriginalMatchingInQueue().includes(versionName);
    }

    isMatchingIdHighlighted(matchingDetailsId: number): boolean {
        const matchingIds = this.pureOriginalMatchingIds();
        return matchingIds && matchingIds.length > 0 && matchingIds.includes(matchingDetailsId);
    }

    startIntervalOnEvery5Seconds() {
        this.pureOriginalsService.startIntervalForEvery5Seconds(this.pureOriginalId());
    }

    changeTuningStatus(event: any, id: string, versionName: string) {
        this.disabledVersionsCheckBox().push(versionName);
        let state = event.valueOf().target.checked
        this.pureOriginalsService.changeImpossibleTunings(id, state).subscribe(response => {
            this.pureOriginalsService.pureOriginalMatchingDetails.set(response);
            this.disabledVersionsCheckBox.update((versions) => versions.filter(versionName => versionName != versionName));
        })
    }

    isDisabledCheckbox(versionName: string) {
        if (this.isClaimedFile()) {
            return true;
        }
        return this.disabledVersionsCheckBox().includes(versionName);
    }

    deleteVersionTuning(objectName: string, id: number, tableName: string) {
        this.pureOriginalsService.deleteVersion(objectName, id, tableName);
    }

    masterDetails(masterId: number, versionName: string) {
        this.router.navigate([`/app/master-details/${masterId}`], {
            queryParams: {
                versionName: versionName
            }
        });
    }

    toggleAttachments(tab: string, index: number) {
        const key = `${tab}-${index}`;
        const wasOpen = this.expandedAttachmentIndex() === key;
        this.expandedAttachmentIndex.update((current) =>
            current === key ? null : key
        );
        if (wasOpen) {
            const list = tab === 'completed' ? this.pagedCompletedList() : this.pagedPartialList();
            const item = list[index];
            if (item?.versionId) {
                this.refreshCountForVersion(+item.versionId);
            }
        }
    }

    private loadAttachmentCounts(items: { versionId: string }[]) {
        const seen = new Set<number>();
        items.forEach((item) => {
            const versionId = +item.versionId;
            if (seen.has(versionId)) return;
            seen.add(versionId);
            this.attachmentService.listAttachments('version', versionId).subscribe({
                next: (attachments) => {
                    this.attachmentCounts.update((m) => {
                        const updated = new Map(m);
                        updated.set(versionId, attachments.length);
                        return updated;
                    });
                }
            });
        });
    }

    private refreshCountForVersion(versionId: number) {
        this.attachmentService.listAttachments('version', versionId).subscribe({
            next: (attachments) => {
                this.attachmentCounts.update((m) => {
                    const updated = new Map(m);
                    updated.set(versionId, attachments.length);
                    return updated;
                });
            }
        });
    }

    ngOnDestroy () {
        this.subscription?.unsubscribe();
        this.pureOriginalsService.clearInterval();
    }
}
